import { colors } from "../../theme/colors";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useTutorial } from "./TutorialContext";
import { TUTORIAL_STEPS } from "./tutorialSteps";
import TutorialStep from "./TutorialStep";

// GUNDI-5665: the spotlight layer. A fixed full-viewport div; the "cutout" is a
// box positioned over the step's target with a 9999px box-shadow that darkens
// everything else. The layer swallows pointer events, so the app cannot be
// clicked mid-tour — the tour is forward-only by product call.
//
// TEMPORARY (≤3 weeks) — see the removal manifest in tutorialInterceptor.ts.
const SPOTLIGHT_PADDING = 8;   // px around the target
const TRACK_MS = 250;          // lifetime re-measure cadence (mount + transitions)
const POLL_TIMEOUT_MS = 8000;  // give up: keep the card in its slot, un-spotlit
const SMALL_TARGET_MAX_H = 200;            // px: targets under this scroll to the upper quarter
const SMALL_TARGET_VIEWPORT_FRACTION = 0.22; // where their top lands, so they clear the centered card

// The card's ONE fixed viewport slot (product call, iterated live): the
// horizontal midpoint between the bottom-left corner and screen center
// (corner left 24px vs center 50vw-170px), vertically centered on the
// viewport (card is 216px tall). Of the fourteen spotlight rects measured,
// only the huge flow-map cutout partially overlaps it — the accepted trade
// for a Next button that never moves.
const CARD_SLOT: React.CSSProperties = { left: "calc(25vw - 73px)", top: "calc(50vh - 108px)" };
// Per-step override (steps opt in via `slot`): to the right of center, upper
// quarter — same viewport-relative method as the default slot, anchored where
// product pointed (~x 1075, y 224 on a 1920×873 reference screen).
const SLOTS: Record<string, React.CSSProperties> = {
    "upper-right": { left: "56vw", top: "26vh" },
};

type Rect = { top: number; left: number; width: number; height: number };

export default function TutorialOverlay() {
    const { t } = useTranslation();
    const { status, stepIndex, advance } = useTutorial();
    const [rect, setRect] = useState<Rect | null>(null);
    const [gaveUp, setGaveUp] = useState(false);

    const step = status === "running" ? TUTORIAL_STEPS[stepIndex] : null;
    const running = !!step;

    // Focus trap (product call, matches forward-only): while the tour runs, the
    // Next button is the ONLY tab stop. The overlay swallows pointer events but
    // Tab would still reach the dimmed app's controls — and Enter there would
    // click a real button through the tour.
    useEffect(() => {
        if (!running) return;
        const focusNext = () => document.querySelector<HTMLElement>('[data-testid="TutorialStep-next-button"]')?.focus();
        focusNext();
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Tab") return;
            e.preventDefault();
            focusNext();
        };
        document.addEventListener("keydown", onKeyDown, true);
        return () => document.removeEventListener("keydown", onKeyDown, true);
    }, [running, stepIndex]);

    useEffect(() => {
        if (!step) return;
        setRect(null);
        setGaveUp(false);
        let elapsed = 0;
        // Scroll the target into a good spot ONCE per step. Small targets go to
        // the upper quarter of the viewport — the card owns the center, and a
        // dead-centered target would sit exactly behind it (the "View
        // Connection" link did). Targets too big for that (the flow map) are
        // centered; the card overlapping a huge cutout is the accepted trade.
        let positioned = false;
        const position = (el: HTMLElement, r: DOMRect) => {
            positioned = true;
            try {
                // Optional call: jsdom has no scrollIntoView.
                el.scrollIntoView?.({ block: "center", inline: "nearest" });
                if (r.height < SMALL_TARGET_MAX_H) {
                    const after = el.getBoundingClientRect();
                    window.scrollBy(0, after.top - window.innerHeight * SMALL_TARGET_VIEWPORT_FRACTION);
                }
            } catch {
                // jsdom implements scrollBy as "not implemented" — a mispositioned
                // page is still a working tour.
            }
        };
        const measure = () => {
            const el = document.querySelector<HTMLElement>(`[data-testid="${step.targetTestId}"]`);
            if (!el) return false;
            // A target that shows up late (after the poll gave up) still gets
            // its ring back — scroll/resize keep calling this.
            setGaveUp(false);
            if (!positioned) position(el, el.getBoundingClientRect());
            const r = el.getBoundingClientRect();
            // Functional update + change check: this runs 4×/second, and an
            // unconditional setRect would re-render the overlay just as often.
            setRect((prev) =>
                prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height
                    ? prev
                    : { top: r.top, left: r.left, width: r.width, height: r.height });
            return true;
        };
        // One interval covers the target's whole life: waiting for it to mount
        // (step pages resolve mock data through effects), AND tracking it once
        // found — slide-in transitions (the destination drawer), toasts and
        // layout shifts all move a target without firing scroll/resize, which
        // is exactly how the drawer's spotlight got measured off-screen once.
        measure();
        const timer = setInterval(() => {
            const found = measure();
            if (!found) {
                elapsed += TRACK_MS;
                if (elapsed >= POLL_TIMEOUT_MS) {
                    clearInterval(timer);
                    setGaveUp(true);
                }
            } else {
                elapsed = 0;
            }
        }, TRACK_MS);
        // Scroll/resize re-measure immediately so the ring never visibly lags.
        const onMove = () => measure();
        window.addEventListener("resize", onMove);
        window.addEventListener("scroll", onMove, true);
        return () => {
            clearInterval(timer);
            window.removeEventListener("resize", onMove);
            window.removeEventListener("scroll", onMove, true);
        };
    }, [step]);

    if (!step) return null;

    // Fixed slot, target found or not — even the un-spotlit fallback stays in
    // the step's slot. Almost every step shares the default slot so the Next
    // button barely moves; a step may opt into a named override.
    const cardStyle = (step.slot && SLOTS[step.slot]) || CARD_SLOT;

    return createPortal(
        // z-[10000]: above EVERYTHING that can appear mid-tour — the destination
        // drawer (z-60, which at equal z painted over the scrim and stayed
        // clickable) and react-toastify's container (z-9999, whose toasts popped
        // fully bright over the dimmed page). The card's own z keeps it above
        // the scrim inside this stacking context.
        <div data-testid="TutorialOverlay-div" className="fixed inset-0 z-[10000]" aria-label={t("Gundi tutorial")}>
            {rect && !gaveUp ? (
                // The cutout: transparent box over the target, everything else
                // darkened by the giant shadow. Rounded to soften the frame.
                // Clicking the spotlit element advances the tour: people
                // instinctively click the thing being described, and the overlay
                // swallows that click anyway — rewarding it beats ignoring it.
                <div
                    data-testid="TutorialOverlay-spotlight"
                    // Pointer-only duplicate of the Next button (people click the
                    // thing being described); hidden from the a11y tree — the
                    // button is the accessible path.
                    aria-hidden="true"
                    onClick={advance}
                    className="absolute rounded-lg transition-all duration-300 cursor-pointer"
                    style={{
                        top: rect.top - SPOTLIGHT_PADDING,
                        left: rect.left - SPOTLIGHT_PADDING,
                        width: rect.width + SPOTLIGHT_PADDING * 2,
                        height: rect.height + SPOTLIGHT_PADDING * 2,
                        // Two layers: a thin light ring that frames the target,
                        // then the scrim. 0.38 = half of the 0.75 it launched
                        // with (product call): the page behind should stay
                        // readable, the ring does the pointing.
                        boxShadow: `0 0 0 2px ${colors["unlisted-tutorial-ring"]}, 0 0 0 9999px ${colors["unlisted-tutorial-scrim"]}`,
                    }}
                />
            ) : (
                <div className="absolute inset-0 bg-greyscale-3 opacity-40" />
            )}
            <TutorialStep
                // `as any`: the key is dynamic, so it cannot be narrowed to
                // i18next's literal key union (repo precedent elsewhere).
                message={t(step.messageKey as any)}
                stepNumber={stepIndex + 1}
                totalSteps={TUTORIAL_STEPS.length}
                onNext={advance}
                isLast={stepIndex === TUTORIAL_STEPS.length - 1}
                style={cardStyle}
            />
        </div>,
        document.body
    );
}
